import { ref, watch, computed } from '@vue/composition-api';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { TABLE, COMPANY } from '@/constants/config';
import { pluck, formatDate, toFixed, formatNumber, formatFloat } from '@/helpers';
import ShareCertificate from '@/models/ShareCertificiate';

export default function useShareCertificatesTable() {
    const toast = useToast();
    const refShareCertificateListTable = ref(null);
    const shareCertificates = ref(null);
    const isPaidUpCapitalMatching = ref(true);

    // Table Handlers
    const tableColumns = [
        // { key: 'index', label: '#', thClass: 'px-1 pr-2', tdClass: 'px-1 pr-2' },
        { key: 'date', label: 'DATE', sortable: true, thClass: 'px-1 pr-2', tdClass: 'px-1 pr-2 text-nowrap' },
        { key: 'name', label: 'NAME', sortable: true, thClass:"px-0 pr-2", tdClass: 'px-0 pr-2' },
        { key: 'numberOfShares', label: 'NO. OF SHARES', sortable: true, thClass: 'px-0 pr-2 text-nowrap', tdClass: 'px-0 pr-2' },
        { key: 'folioNumber', label: 'REF NO.', sortable: true, thClass: 'px-0 pr-2 text-nowrap', tdClass: 'px-0 pr-2'},
        { key: 'number', label: 'CERT NO.', sortable: true, thClass: 'px-0 pr-2 text-nowrap', tdClass: 'px-0 pr-2' },
        { key: 'status', label: 'STATUS', sortable: true, thClass: 'px-0 pr-2 text-nowrap', tdClass: 'px-0 pr-2' },
        { key: 'shareType.name', label: 'TYPE', sortable: true, thClass: 'px-0 pr-2 text-nowrap', tdClass: 'px-0 pr-2' },
        { key: 'actions', label: 'ACTION', thClass: 'pl-0 pr-1 text-right', tdClass: 'pl-0 pr-1 text-right' },
    ];

    const perPage = ref(TABLE.PER_PAGE);
    const totalShareCertificates = ref(0);
    const currentPage = ref(1);
    const perPageOptions = TABLE.PER_PAGE_OPTIONS;
    const searchQuery = ref('');
    const sortBy = ref('id');
    const isSortDirDesc = ref(true);
    const isBusy = ref(false);
    const nextCertificateNumber = ref(0);

    // local variable to store the companies list
    const localShareCertificates = ref([]);

    const isLoading = ref(false);

    // options where the search query is applied
    // make sure to list as keys of how to access those values in table 
    const filterOn = ref([
        'date',
        'name',
        'folioNumber'
    ]);

    const dataMeta = computed(() => {
        const localItemsCount = refShareCertificateListTable.value ? refShareCertificateListTable.value.localItems.length : 0;
        totalShareCertificates.value = localItemsCount;
        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: totalShareCertificates.value,
        };
    });
   
    // to change api data to straightforward easy, js, local data
    const localizeData = data => ({
        id: data.id,
        date: formatDate(data.date),
        name: data.user?.user_info?.legal_name ?? data.user?.name,
        numberOfShares: formatNumber(data.number_of_shares),
        folioNumber: data.folio_number,
        nature: {
            text: data.nature,
            value: data.nature
        },
        number: data.number,
        status: data.status,
        shareType: data.share_capital.share_type,
        currency: data.share_capital.currency,
        shareholder: data.user,
        paidAmount: formatFloat(toFixed(data.paid_up_share_capital)),
        payableAmount: data.payable_amount >= 1 ? formatFloat(toFixed(data.payable_amount)) : data.payable_amount,
        paymentStatus: data.payment_status,
    });

    const fetchShareCertificates = () => {
        isLoading.value = true;
        shareCertificates.value = [];

        ShareCertificate.all().then(certificates => {
            localShareCertificates.value = certificates.data.map(certificate => localizeData(certificate));
            nextCertificateNumber.value = certificates?.meta?.next_certificate_number;
            isPaidUpCapitalMatching.value = certificates?.meta?.is_paid_up_capital_matching;
            
            shareCertificates.value = localShareCertificates.value;
            
            isLoading.value = false;
        })
            .catch(e => {
                console.error("Error fetching share certificates", e);

                toast({
                    component: ToastificationContent,
                    props: {
                        title: 'Error fetching share certificate list',
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                    },
                });
            });
    };

    fetchShareCertificates();

    const refetchData = () => {
        refShareCertificateListTable.value.refresh();
    };

    watch([currentPage, perPage, searchQuery], () => {
        refetchData();
        
        let data = localShareCertificates.value;
        const queryLowered = searchQuery.value.toLowerCase().trim();

        //  if a query present then search
        if (queryLowered)
        {
            data = localShareCertificates.value.filter(item => filterOn.value.some(key => {
                const value = pluck(key, item);
                return value && value.toString().toLowerCase().trim().includes(queryLowered);
            }));
        }

        refShareCertificateListTable.value.localItems = data;
        isBusy.value = false;
    });

    const resolveShareCertificateStatusVariant = status => {
        if (status === COMPANY.SHARE_CERTIFICATE.STATUS.CANCELLED) return 'danger';
        if (status === COMPANY.SHARE_CERTIFICATE.STATUS.ACTIVE) return 'success';
        if (status === COMPANY.SHARE_CERTIFICATE.STATUS.INACTIVE) return 'secondary';
        return 'primary';
    };

    return {
        sortBy,
        isBusy,
        perPage,
        dataMeta,
        isLoading,
        searchQuery,
        currentPage,
        tableColumns,
        isSortDirDesc,
        totalShareCertificates,
        perPageOptions,
        refShareCertificateListTable,
        isPaidUpCapitalMatching,

        shareCertificates,
        refetchData,
        fetchShareCertificates,
        resolveShareCertificateStatusVariant,
        filterOn,
        localizeData,
        nextCertificateNumber
    };
}
